/**
 * PII masking primitives that read from the ``privacy`` redux slice and
 * decide whether to render the real value or a placeholder.
 *
 * Two switches govern these primitives:
 * - ``piiMode``: identity / contact / ID fields
 * - ``privacyMode``: clinical note bodies (rich-text)
 *
 * Both are session-only shoulder-surf protections; the BACKEND remains
 * the source of truth for cross-user visibility (see the API repo's
 * ``ClinicalNote.hide_from_client`` / ``is_sensitive`` filters and the
 * ``UserProfile`` serializer's role-aware field stripping).
 */
import { Eye, User as UserIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { cn } from '@/lib/utils';
import {
  initialsOf,
  maskAddress,
  maskDate,
  maskEmail,
  maskGovernmentId,
  maskName,
  maskPhone,
  NOTE_HIDDEN_PLACEHOLDER,
} from '@/utils/piiMask';

export type PiiKind =
  | 'name'
  | 'email'
  | 'phone'
  | 'address'
  | 'date'
  | 'id'
  | 'image'
  | 'html';

/**
 * Identity / contact / ID masking. Flips on ``piiMode``.
 *
 * ``name`` accepts either ``value="John Doe"`` or the more accurate
 * ``firstName`` / ``lastName`` props (preferred; produces ``J. D.``).
 */
type PiiMaskProps = {
  kind: PiiKind;
  value?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** Optional fallback content when value is null/empty AND mask is OFF. */
  fallback?: ReactNode;
  className?: string;
};

export const PiiMask = ({
  kind,
  value,
  firstName,
  lastName,
  fallback,
  className,
}: PiiMaskProps) => {
  const piiMode = useSelector((s: RootState) => s.privacy?.piiMode ?? false);
  const privacyMode = useSelector((s: RootState) => s.privacy?.privacyMode ?? false);

  // ``html`` kind is governed by privacyMode (note bodies); the rest by piiMode.
  if (kind === 'html') {
    return <PrivacyHtml html={value ?? ''} active={privacyMode} className={className} />;
  }

  if (!piiMode) {
    if (kind === 'name' && (firstName || lastName)) {
      return <span className={className}>{[firstName, lastName].filter(Boolean).join(' ')}</span>;
    }
    if (value) return <span className={className}>{value}</span>;
    return <>{fallback ?? null}</>;
  }

  if (kind === 'image') {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full bg-gray-200 text-gray-500',
          className,
        )}
        aria-label="PII masked"
      >
        <UserIcon className="h-1/2 w-1/2" />
      </span>
    );
  }

  let masked: string;
  switch (kind) {
    case 'name':
      masked = firstName || lastName ? initialsOf(firstName, lastName) : maskName(value);
      break;
    case 'email':
      masked = maskEmail(value);
      break;
    case 'phone':
      masked = maskPhone(value);
      break;
    case 'address':
      masked = maskAddress(value);
      break;
    case 'date':
      masked = maskDate(value);
      break;
    case 'id':
      masked = maskGovernmentId(value);
      break;
    default:
      masked = '•••';
  }

  return (
    <span className={cn('select-none', className)} aria-label="PII masked">
      {masked}
    </span>
  );
};

/**
 * Click-to-reveal wrapper for clinical note ``content_html``. Reveals
 * are local to the component instance and reset when the toggle flips
 * back on or the component unmounts.
 */
type PrivacyHtmlProps = {
  html: string;
  active: boolean;
  className?: string;
};

const PrivacyHtml = ({ html, active, className }: PrivacyHtmlProps) => {
  const [revealed, setRevealed] = useState(false);

  if (!active || revealed) {
    return (
      <div
        className={className}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setRevealed(true)}
      className={cn(
        'flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-6 text-sm text-gray-500 hover:bg-gray-100 w-full justify-center',
        className,
      )}
    >
      <Eye className="h-4 w-4" />
      {NOTE_HIDDEN_PLACEHOLDER}
    </button>
  );
};

/**
 * Generic click-to-reveal gate for arbitrary children that should be
 * hidden when ``privacyMode`` is on. Use this when you want to keep an
 * existing renderer (e.g. ``ClinicalNoteRenderer`` with its own
 * sanitization) and just gate visibility — unlike ``<PiiMask kind="html">``
 * which renders the raw HTML itself.
 *
 *   <PrivacyGate>
 *     <ClinicalNoteRenderer html={note.content_html} />
 *   </PrivacyGate>
 */
type PrivacyGateProps = {
  children: ReactNode;
  className?: string;
  /** Override default placeholder text. */
  placeholder?: ReactNode;
};

export const PrivacyGate = ({ children, className, placeholder }: PrivacyGateProps) => {
  const privacyMode = useSelector((s: RootState) => s.privacy?.privacyMode ?? false);
  const [revealed, setRevealed] = useState(false);

  if (!privacyMode || revealed) {
    return <>{children}</>;
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        // Stop event from bubbling — the parent ClinicalNoteCard treats
        // clicks as "edit draft" and would intercept this tap otherwise.
        e.stopPropagation();
        setRevealed(true);
      }}
      className={cn(
        'flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-6 text-sm text-gray-500 hover:bg-gray-100 w-full justify-center',
        className,
      )}
    >
      <Eye className="h-4 w-4" />
      {placeholder ?? NOTE_HIDDEN_PLACEHOLDER}
    </button>
  );
};

export default PiiMask;
